import { useSelector } from "react-redux";
import { AppState } from "store";
import { EnvironmentEnums } from "models/enums";
import { EnvHelper } from "utils/helpers";

// Who sees the agent: on test and locally all companies, anywhere else (production, or a build whose
// environment is missing) only these. An empty list opens it to every company in production too, so
// closing it again takes a client deploy; the server's kill switch stays the quick way off.
const AgentCompanyIds: number[] = [1007, 2391, 7426, 29895, 12214, 434, 6464, 168];

const OpenToAll: string[] = [EnvironmentEnums.Environment.Test, EnvironmentEnums.Environment.Development];

// Set to "1" in localStorage to get the old help flow in any environment - the way to try the support
// form, and the background run behind it (see useAgentShadow), on test, where everyone has the chat.
const OldFlowOverrideKey = "agentOldFlow";

const isOldFlowForced = () => {
  try {
    return localStorage.getItem(OldFlowOverrideKey) === "1";
  } catch {
    return false;
  }
};

export const agentAccessFor = (companyId: number | undefined, companyIds: number[], environment: string) =>
  OpenToAll.includes(environment) ||
  companyIds.length === 0 ||
  (companyId !== undefined && companyIds.includes(companyId));

export const hasAgentAccess = (companyId: number | undefined) =>
  !isOldFlowForced() && agentAccessFor(companyId, AgentCompanyIds, EnvHelper.getEnvironment());

/**
 * Whether the selected company gets the agent. Never at a Byråsupport company: its case goes to the recipient she
 * picks (her byrå or Wint), and only the support form asks for one. Until the company's support options are in,
 * it may be one, so the agent stays closed.
 */
export const useAgentAccess = (): boolean => {
  const companyId = useSelector((state: AppState) => state.auth.selectedCompany.id);
  const options = useSelector((state: AppState) => state.common.supportCategoryOptions);
  return options?.companyId === companyId && !options.agencyMode && hasAgentAccess(companyId);
};
